import {
  Box,
  Button,
  Dialog,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Typography,
  CircularProgress
} from '@mui/material'
import { useCallback, useContext, useEffect, useState } from 'react'
import { HistoriasPublicadasAhora, ItemList } from '../../../AdminLocal/types'
import { GridCloseIcon } from '@mui/x-data-grid'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ControlPointDuplicateOutlinedIcon from '@mui/icons-material/ControlPointDuplicateOutlined'
import { eliminarHistoriaVendedor } from '../../../AdminHistorias/services/historias.services'
import { TenantsContext } from '../../../../context/tenants.context'
import { useAnalytics } from '../../../../hooks/useAnalytics'
import SnackBarUtils from '../../../../utils/SnackBarUtils'

interface DialogVerHistoriasProps {
  open: boolean
  setOpen: (op: boolean) => void
  historias: HistoriasPublicadasAhora
  isMobile: boolean
  codLocal: string
  crearHistoria: (crear: boolean) => void
  updateHistorias: (codLocal: string) => Promise<void>
}

const DialogVerHistorias = ({
  open,
  setOpen,
  historias,
  isMobile,
  codLocal,
  crearHistoria,
  updateHistorias
}: DialogVerHistoriasProps) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [currentIndex, setCurrentIndex] = useState<number>(historias.primerIndexSinLeer || 0)
  const [progress, setProgress] = useState<number>(0)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [imageLoading, setImageLoading] = useState<boolean>(true)
  const [disableProgressAnimation, setDisableProgressAnimation] = useState(false)
  const { tenant } = useContext(TenantsContext)
  const { track } = useAnalytics()

  const handleDelete = async (idHistoria: number) => {
    if (!idHistoria) return

    try {
      setLoading(true)
      await eliminarHistoriaVendedor(idHistoria, tenant)
      SnackBarUtils.success('Historia eliminada con éxito')
      track('vendor_story_delete', { id_historia: idHistoria })
    } catch (e) {
      console.log(e.response)
    } finally {
      setIsDeleting(false)
      updateHistorias(codLocal)
      handleClose()
    }
  }

  const handleClose = () => {
    setLoading(false)
    setAnchorEl(null)
    setOpen(false)
    setCurrentIndex(0)
    setProgress(0)
  }

  const handleClick = useCallback(
    (e: any) => {
      const { nativeEvent } = e
      const rect = e.currentTarget.getBoundingClientRect()
      const clickX = nativeEvent.clientX - rect.left
      const middle = rect.width / 2

      setProgress(0)
      setImageLoading(true)
      setDisableProgressAnimation(true)

      if (clickX < middle) {
        handlePreviousStory()
      } else {
        handleNextStory()
      }
    },
    [currentIndex, historias.itemList.length, setOpen]
  )

  const handleNextStory = () => {
    if (currentIndex < historias.itemList.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1)
    } else {
      handleClose()
    }
  }

  const handlePreviousStory = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1)
    } else {
      setCurrentIndex(0)
      setImageLoading(false)
    }
  }

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (open && !imageLoading && !isDeleting) {
      interval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            handleNextStory()
            return 0
          }
          return prevProgress + 100 / (5 * 20)
        })
      }, 50)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [open, isDeleting, imageLoading, handleNextStory])

  useEffect(() => {
    if (!open) {
      setCurrentIndex(0)
      setProgress(0)
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      PaperProps={{
        style: {
          display: 'flex',
          width: isMobile ? '100%' : '600px',
          height: isMobile ? '100%' : '800px',
          backgroundColor: 'black',
          margin: 0,
          overflow: 'hidden'
        }
      }}
    >
      <div style={{ position: 'absolute', top: 10, right: 2 }}>
        <IconButton
          aria-label="close"
          onClick={(e) => {
            setAnchorEl(e.currentTarget)
            setIsDeleting(!isDeleting)
          }}
          sx={{
            fontSize: '8px',
            color: 'white'
          }}
        >
          <MoreVertIcon />
        </IconButton>

        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            fontSize: '8px',
            color: 'white'
          }}
        >
          <GridCloseIcon />
        </IconButton>
      </div>
      <Box onClick={(e) => handleClick(e)} sx={{ padding: 0, width: '100%', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            padding: '10px'
          }}
        >
          {historias.itemList.map((_, index) => (
            <LinearProgress
              key={index}
              variant="determinate"
              value={index < currentIndex ? 100 : index > currentIndex ? 0 : progress}
              sx={{
                flex: 1,
                height: '3px',
                margin: '0 2px',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'white',
                  transition: disableProgressAnimation ? 'none' : undefined
                }
              }}
            />
          ))}
        </Box>

        {/* Indicador de carga mientras la imagen se carga */}
        {(imageLoading || loading) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}
          onClick={() => setIsDeleting(false)}
        >
          <img
            src={historias.itemList[currentIndex]?.fotoUrl}
            alt={historias.itemList[currentIndex]?.titulo}
            style={{
              objectFit: 'contain',
              width: '100%',
              maxHeight: '100%'
            }}
            onLoad={() => setImageLoading(false)}
          />
        </Box>
      </Box>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => handleDelete(historias.itemList[currentIndex]?.id)}>Eliminar</MenuItem>
      </Menu>
      <Button
        variant="contained"
        sx={{
          position: 'absolute',
          bottom: 20,
          width: isMobile ? '70%' : '50%',
          alignSelf: 'center',
          gap: '10px',
          border: '1px solid white',
          borderRadius: '50px',
          padding: '10px 20px'
        }}
        onClick={() => {
          crearHistoria(true)
          handleClose()
        }}
      >
        <ControlPointDuplicateOutlinedIcon fontSize={isMobile ? 'medium' : 'large'} />
        <Typography style={{ fontSize: isMobile ? '12px' : '14px' }}>Agregar otra historia</Typography>
      </Button>
    </Dialog>
  )
}

export default DialogVerHistorias
